import type { Table } from '@tanstack/react-table';
import type { DynamicTableColumnConfig, DynamicTableKanbanConfig } from './dynamic-table-types';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { getDisplayLabel, renderBadge, type ColumnMeta } from './DynamicTableValueRenderers';

export interface DynamicTableKanbanViewProps<T extends object> {
  table: Table<T>;
  columns: DynamicTableColumnConfig<T>[];
  kanbanConfig: DynamicTableKanbanConfig<T>;
  isLoading: boolean;
  loadingMessage: string;
  emptyMessage: string;
  onRowClick?: (row: T, index: number) => void;
}

const toGroupKey = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return String(o.name ?? o.id ?? '');
  }
  return String(value);
};

export function DynamicTableKanbanView<T extends object>({
  table,
  columns,
  kanbanConfig,
  isLoading,
  loadingMessage,
  emptyMessage,
  onRowClick,
}: DynamicTableKanbanViewProps<T>) {
  const data = table.getCoreRowModel().rows.map(r => r.original);
  const getRowId = (row: T) =>
    table.options.getRowId
      ? table.options.getRowId(row, 0, undefined)
      : String((row as { id?: string }).id ?? '');

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center gap-2 py-12 text-muted-foreground">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="animate-spin"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          {loadingMessage}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="p-4">
        <EmptyStatePanel message={emptyMessage} />
      </div>
    );
  }

  const groupKey = kanbanConfig.groupBy as string;
  const groups = new Map<string, T[]>();

  for (const row of data) {
    const val = (row as Record<string, unknown>)[groupKey];
    const key = toGroupKey(val);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  const lanes =
    kanbanConfig.laneOrder ?? Array.from(groups.keys()).sort((a, b) => a.localeCompare(b));

  const limitedLanes = kanbanConfig.maxLanes ? lanes.slice(0, kanbanConfig.maxLanes) : lanes;

  return (
    <div className="p-4">
      <div className="flex gap-4 overflow-x-auto pb-2">
        {limitedLanes.map(laneValue => (
          <div
            key={laneValue || '__empty__'}
            className="flex-shrink-0 w-[280px] rounded-lg border border-border bg-muted/30"
          >
            <div className="px-3 py-2 border-b border-border font-medium text-foreground">
              {laneValue || '(Empty)'}
            </div>
            <div className="p-2 space-y-2 min-h-[120px]">
              {(groups.get(laneValue) ?? []).map((row, index) => (
                <div
                  key={getRowId(row)}
                  role={onRowClick ? 'button' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={() => onRowClick?.(row, index)}
                  onKeyDown={e => onRowClick && e.key === 'Enter' && onRowClick(row, index)}
                  className={`rounded-md border border-border bg-card p-3 shadow-sm text-sm ${onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                >
                  {columns
                    .filter(c => c.id !== 'actions' && c.id !== groupKey)
                    .slice(0, 3)
                    .map(col => {
                      const accessorKey = (col.accessorKey ?? col.id) as string;
                      const value = (row as Record<string, unknown>)[accessorKey];
                      return (
                        <div key={col.id} className="truncate">
                          {col.cell
                            ? col.cell({ row, value, index })
                            : col.columnType === 'badge'
                              ? renderBadge(
                                  value,
                                  (col.meta as ColumnMeta | undefined) ?? undefined
                                )
                              : (getDisplayLabel(
                                  value,
                                  (col.meta as ColumnMeta | undefined) ?? undefined
                                ) ?? (value == null ? '—' : String(value)))}
                        </div>
                      );
                    })}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
